import type {
  BoardPoint,
  FoxKifuAiAnalysis,
  FoxKifuAiVariationData,
  StoneColor,
} from '@wx-weiqi/contracts';

const PRIMARY_CANDIDATE_APPEARANCE = Object.freeze({
  fill: '#174b96',
  text: '#ffffff',
  stroke: '#ffffff',
});
const PASTEL_CANDIDATE_APPEARANCES = [
  Object.freeze({ fill: '#a8dfb7', text: '#132e3b', stroke: '#44815a' }),
  Object.freeze({ fill: '#9dd3f5', text: '#132e3b', stroke: '#407fa8' }),
  Object.freeze({ fill: '#c5b9eb', text: '#132e3b', stroke: '#79659c' }),
  Object.freeze({ fill: '#edb7cb', text: '#132e3b', stroke: '#a4637f' }),
  Object.freeze({ fill: '#9eddd8', text: '#132e3b', stroke: '#427f7a' }),
] as const;

/** Pastels distinguish alternatives; rank, not hue, conveys recommendation order. */
export function aiCandidateAppearance(rank: number) {
  if (rank === 1) return PRIMARY_CANDIDATE_APPEARANCE;
  return (
    PASTEL_CANDIDATE_APPEARANCES[(rank - 2) % PASTEL_CANDIDATE_APPEARANCES.length] ??
    PASTEL_CANDIDATE_APPEARANCES[0]
  );
}

export interface AiCandidateMarkerLayout {
  readonly circleRadius: number;
  readonly rateFontSize: number;
  readonly rankFontSize: number;
  readonly rateOffsetY: number;
  readonly rankOffsetY: number;
}

export function aiCandidateMarkerLayout(stoneRadius: number): AiCandidateMarkerLayout {
  return {
    circleRadius: stoneRadius * 0.9,
    rateFontSize: Math.min(15, stoneRadius * 0.44),
    rankFontSize: Math.min(30, stoneRadius * 0.88),
    rateOffsetY: stoneRadius * 0.55,
    rankOffsetY: stoneRadius * -0.25,
  };
}

/** Fit unusually wide values without dropping digits. */
export function aiCandidateValueFontSize(baseSize: number, value: string, regularLength: number) {
  return baseSize * Math.min(1, regularLength / value.length);
}

export interface AiCandidatePresentation {
  readonly point: BoardPoint;
  readonly rank: number;
  readonly winRateBps: number;
  readonly scoreLeadHalfPoints: number;
  readonly visits: number;
  readonly color: StoneColor;
}

export function presentAiCandidates(
  analysis: FoxKifuAiAnalysis | null,
): readonly AiCandidatePresentation[] {
  if (analysis?.result === null || analysis === null) return [];
  const perspective = analysis.nextColor === 'BLACK' ? 1 : -1;
  return analysis.result.bestMoves.slice(0, 10).flatMap((candidate, index) =>
    candidate.move.kind === 'PLAY'
      ? [
          {
            point: candidate.move.point,
            rank: index + 1,
            winRateBps:
              analysis.nextColor === 'BLACK' ? candidate.winRateBps : 10_000 - candidate.winRateBps,
            scoreLeadHalfPoints: candidate.scoreLeadHalfPoints * perspective,
            visits: candidate.visits,
            color: analysis.nextColor,
          },
        ]
      : [],
  );
}

export function formatAiVisits(visits: number): string {
  if (visits >= 1_000_000) return `${(visits / 1_000_000).toFixed(1)}m`;
  if (visits >= 1_000) return `${(visits / 1_000).toFixed(1)}k`;
  return String(visits);
}

export function formatAiScoreLead(scoreLeadHalfPoints: number): string {
  return (scoreLeadHalfPoints / 2).toFixed(1);
}

export function aiVariationStoneLabels(variation: FoxKifuAiVariationData | null) {
  if (variation === null) return [];
  const latest = new Map<
    string,
    {
      readonly point: BoardPoint;
      readonly label: string;
      readonly color: StoneColor;
      readonly moveNumber: number;
    }
  >();
  for (const frame of variation.frames) {
    if (frame.kind !== 'PLAY' || frame.point === null) continue;
    if (variation.stones[frame.point.y * variation.boardSize + frame.point.x] !== frame.color) {
      continue;
    }
    latest.set(`${frame.point.x}:${frame.point.y}`, {
      point: frame.point,
      label: String(frame.moveNumber),
      color: frame.color,
      moveNumber: frame.moveNumber,
    });
  }
  return [...latest.values()]
    .sort((left, right) => left.moveNumber - right.moveNumber)
    .map(({ point, label, color }) => ({ point, label, color }));
}
